/**
 * Money settings for system admins (design 3.10, step M4): the workspace's currency, its cost
 * categories and the rate cards internal labour is estimated with. Categories and rate cards are
 * archived, never deleted, so estimates and records keep pointing at them.
 */
import { useState, type FormEvent } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { CostCategory, FinanceSettings, RateCard, type CostCategoryKind, type RateUnit } from "@workcore/schemas";
import { checkedWrite, get } from "../../api";
import { wsKey } from "../../directory";
import { HistoryLink } from "../../activity";
import { ErrorBox, PageHeader, Panel, RowMenu, toast } from "../../ui";
import { InlineText } from "../editor/shared";
import { fmt, toAmount, toPence } from "../../money";

const toKey = (label: string) => label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").replace(/^(\d)/, "c_$1") || "category";
const KIND_LABEL: Record<CostCategoryKind, string> = { internal_labour: "internal labour (priced from rate cards)", external: "external", other: "other" };

function useWrite(slug: string) {
  const q = useQueryClient();
  return useMutation({
    mutationFn: ({ method, path, body }: { method: string; path: string; body?: unknown }) => checkedWrite(method, path, body),
    onSuccess: () => toast("Saved"),
    onSettled: () => q.invalidateQueries({ queryKey: ["ws", slug] }),
  });
}

export function MoneySettingsPage() {
  const { slug = "" } = useParams();
  return (
    <>
      <PageHeader title="Money" description="The currency, the cost categories estimates and invoices are filed under, and the rates internal labour is estimated at." actions={<HistoryLink slug={slug} kind="money" />} />
      <Currency slug={slug} />
      <Categories slug={slug} />
      <Rates slug={slug} />
    </>
  );
}

function Currency({ slug }: { slug: string }) {
  const s = useQuery({ queryKey: wsKey(slug, "finance-settings"), queryFn: async () => FinanceSettings.parse(await get(`/v1/ws/${slug}/finance-settings`)) });
  const write = useWrite(slug);
  return (
    <Panel title="Currency">
      <ErrorBox error={s.error ?? write.error} />
      {s.data && (
        <p>
          All money in this workspace is in{" "}
          <InlineText label="currency" value={s.data.currency} onSave={(v) => write.mutate({ method: "PATCH", path: `/v1/ws/${slug}/finance-settings`, body: { currency: v.trim().toUpperCase() } })} />
          <span className="muted small"> (a three-letter code such as GBP; fixed once money is recorded)</span>
        </p>
      )}
    </Panel>
  );
}

function Categories({ slug }: { slug: string }) {
  const list = useQuery({ queryKey: wsKey(slug, "cost-categories-all"), queryFn: async () => z.array(CostCategory).parse(await get(`/v1/ws/${slug}/cost-categories`, { all: "true" })) });
  const write = useWrite(slug);
  const [label, setLabel] = useState("");
  const [kind, setKind] = useState<CostCategoryKind>("other");
  const add = (e: FormEvent) => {
    e.preventDefault();
    write.mutate({ method: "POST", path: `/v1/ws/${slug}/cost-categories`, body: { key: toKey(label), label, kind } }, { onSuccess: () => setLabel("") });
  };
  return (
    <Panel title="Cost categories">
      <ErrorBox error={list.error ?? write.error} />
      <table className="grid">
        <thead><tr><th>Category</th><th>Key</th><th>Kind</th><th /></tr></thead>
        <tbody>
          {(list.data ?? []).map((c) => (
            <tr key={c.id} className={c.archived ? "inactive" : ""}>
              <td><InlineText label={`${c.label} label`} value={c.label} onSave={(v) => write.mutate({ method: "PATCH", path: `/v1/ws/${slug}/cost-categories/${c.id}`, body: { label: v } })} />{c.archived && <span className="muted small"> archived</span>}</td>
              <td><code>{c.key}</code></td>
              <td className="muted">{KIND_LABEL[c.kind]}</td>
              <td className="row-actions"><RowMenu label={`Actions for ${c.label}`} items={[{ label: c.archived ? "Unarchive" : "Archive", onSelect: () => write.mutate({ method: "PATCH", path: `/v1/ws/${slug}/cost-categories/${c.id}`, body: { archived: !c.archived } }) }]} /></td>
            </tr>
          ))}
        </tbody>
      </table>
      <form autoComplete="off" className="inline wrap" onSubmit={add}>
        <label className="inline-field"><span>New category</span><input aria-label="New cost category" required placeholder="e.g. Plant hire" value={label} onChange={(e) => setLabel(e.target.value)} /></label>
        <label className="inline-field"><span>Kind</span><select aria-label="New cost category kind" value={kind} onChange={(e) => setKind(e.target.value as CostCategoryKind)}>
          <option value="other">other</option>
          <option value="external">external</option>
          <option value="internal_labour">internal labour</option>
        </select></label>
        <button type="submit">Add category</button>
      </form>
      <p className="muted small">Keys never change; labels can. Lines in an internal labour category are priced from a rate card.</p>
    </Panel>
  );
}

function Rates({ slug }: { slug: string }) {
  const list = useQuery({ queryKey: wsKey(slug, "rate-cards-all"), queryFn: async () => z.array(RateCard).parse(await get(`/v1/ws/${slug}/rate-cards`, { all: "true" })) });
  const write = useWrite(slug);
  const [label, setLabel] = useState("");
  const [unit, setUnit] = useState<RateUnit>("day");
  const [cost, setCost] = useState("");
  const pence = toPence(cost);
  const add = (e: FormEvent) => {
    e.preventDefault();
    if (pence === null) return;
    write.mutate({ method: "POST", path: `/v1/ws/${slug}/rate-cards`, body: { key: toKey(label), label, unit, costPence: pence } }, { onSuccess: () => (setLabel(""), setCost("")) });
  };
  return (
    <Panel title="Rate cards">
      <ErrorBox error={list.error ?? write.error} />
      {(list.data ?? []).length === 0 ? (
        <p className="muted">No rates yet. A rate is the cost of a role, not a person, so nobody's pay is shown.</p>
      ) : (
        <table className="grid">
          <thead><tr><th>Role</th><th>Cost</th><th /></tr></thead>
          <tbody>
            {(list.data ?? []).map((r) => (
              <tr key={r.id} className={r.archived ? "inactive" : ""}>
                <td><InlineText label={`${r.label} role`} value={r.label} onSave={(v) => write.mutate({ method: "PATCH", path: `/v1/ws/${slug}/rate-cards/${r.id}`, body: { label: v } })} />{r.archived && <span className="muted small"> archived</span>}</td>
                <td>
                  <InlineText
                    label={`${r.label} cost`}
                    value={toAmount(r.costPence)}
                    onSave={(v) => {
                      const p = toPence(v);
                      if (p === null) toast("Not an amount: use a number like 650 or 82.50");
                      else write.mutate({ method: "PATCH", path: `/v1/ws/${slug}/rate-cards/${r.id}`, body: { costPence: p } });
                    }}
                  />
                  <span className="muted"> per {r.unit} ({fmt(r.costPence)})</span>
                </td>
                <td className="row-actions"><RowMenu label={`Actions for ${r.label}`} items={[{ label: r.archived ? "Unarchive" : "Archive", onSelect: () => write.mutate({ method: "PATCH", path: `/v1/ws/${slug}/rate-cards/${r.id}`, body: { archived: !r.archived } }) }]} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <form autoComplete="off" className="inline wrap" onSubmit={add}>
        <label className="inline-field"><span>Role</span><input aria-label="New rate role" required placeholder="e.g. Senior engineer" value={label} onChange={(e) => setLabel(e.target.value)} /></label>
        <label className="inline-field"><span>Cost</span><input aria-label="New rate cost" required inputMode="decimal" placeholder="650.00" value={cost} onChange={(e) => setCost(e.target.value)} /></label>
        <label className="inline-field"><span>Per</span><select aria-label="New rate unit" value={unit} onChange={(e) => setUnit(e.target.value as RateUnit)}><option value="day">day</option><option value="hour">hour</option></select></label>
        <button type="submit" disabled={pence === null}>Add rate</button>
      </form>
      <p className="muted small">Changing a rate never changes an estimate already made: a line copies the rate when it is added.</p>
    </Panel>
  );
}
